import React, { useState } from 'react';
import ReactPaginate from 'react-paginate';
import ConfirmationDialog from './ConfirmationDialog';
import { FaUsers, FaEdit, FaKey, FaTrash, FaCheck } from 'react-icons/fa';
import './UserManagement.css';

const ITEMS_PER_PAGE = 5;
const AVAILABLE_MODULES = [
  'Deck',
  'Comment Monitoring',
  'Location Search',
  'NOTAM',
  'Relation Graph',
  'Originator Tracker',
  'Dark Hawk',
  'Admin Panel',
  'Profiling',
  'Analytics'
];

const UserManagement = ({ users, onUpdatePermissions, onUpdateUser, onDeleteUser }) => {
  const [pageNumber, setPageNumber] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const [editingCredentials, setEditingCredentials] = useState(null);
  const [tempCredentials, setTempCredentials] = useState({ email: '', password: '' });
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const pageCount = Math.ceil(users.length / ITEMS_PER_PAGE);
  const offset = pageNumber * ITEMS_PER_PAGE;
  const currentPageUsers = users.slice(offset, offset + ITEMS_PER_PAGE);

  const handlePageClick = ({ selected }) => {
    setPageNumber(selected);
  };

  const handlePermissionToggle = (userId, module) => {
    const user = users.find(u => u.id === userId);
    const updatedPermissions = user.permissions.includes(module)
      ? user.permissions.filter(p => p !== module)
      : [...user.permissions, module];
    
    setConfirmDialog({
      isOpen: true,
      title: 'Update Permissions',
      message: `Are you sure you want to ${user.permissions.includes(module) ? 'remove' : 'add'} ${module} permission for ${user.name}?`,
      onConfirm: () => {
        onUpdatePermissions(userId, updatedPermissions);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  const handleStartEditing = (user) => {
    setEditingCredentials(user.id);
    setTempCredentials({
      email: user.email,
      password: user.password
    });
  };

  const handleCredentialChange = (field, value) => {
    setTempCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveCredentials = (userId) => {
    const user = users.find(u => u.id === userId);
    setConfirmDialog({
      isOpen: true,
      title: 'Update Credentials',
      message: `Are you sure you want to update the credentials for ${user.name}?`,
      onConfirm: () => {
        onUpdateUser(userId, tempCredentials);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        setEditingCredentials(null);
      }
    });
  };

  const handleDeleteClick = (userId) => {
    const user = users.find(u => u.id === userId);
    setConfirmDialog({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to delete ${user.name}? This action cannot be undone.`,
      onConfirm: () => {
        onDeleteUser(userId);
        setConfirmDialog({ ...confirmDialog, isOpen: false });
      }
    });
  };

  return (
    <div className="super-admin-management">
      <h2><FaUsers className="section-icon" /> User Management</h2>
      <div className="super-admin-table-container">
        <table className="super-admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Password</th>
              <th>Status</th>
              <th>Permissions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentPageUsers.map(user => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>
                  {editingCredentials === user.id ? (
                    <input
                      type="email"
                      value={tempCredentials.email}
                      onChange={(e) => handleCredentialChange('email', e.target.value)}
                      className="super-admin-edit-input"
                    />
                  ) : (
                    user.email
                  )}
                </td>
                <td>
                  {editingCredentials === user.id ? (
                    <input
                      type="text"
                      value={tempCredentials.password}
                      onChange={(e) => handleCredentialChange('password', e.target.value)}
                      className="super-admin-edit-input"
                    />
                  ) : (
                    user.password
                  )}
                </td>
                <td>
                  <span className="super-admin-status-approved">
                    <FaCheck /> Approved
                  </span>
                </td>
                <td>
                  <div className="super-admin-permissions">
                    {user.permissions.join(', ') || 'None'}
                  </div>
                </td>
                <td>
                  <div className="super-admin-actions">
                    <button 
                      className="super-admin-edit-btn"
                      onClick={() => setEditingUser(editingUser === user.id ? null : user.id)}
                    >
                      <FaEdit /> {editingUser === user.id ? 'Close' : 'Edit Permissions'}
                    </button>
                    <button 
                      className="super-admin-edit-credentials-btn"
                      onClick={() => {
                        if (editingCredentials === user.id) {
                          handleSaveCredentials(user.id);
                        } else {
                          handleStartEditing(user);
                        }
                      }}
                    >
                      <FaKey /> {editingCredentials === user.id ? 'Save' : 'Edit Credentials'}
                    </button>
                    <button 
                      className="super-admin-delete-btn"
                      onClick={() => handleDeleteClick(user.id)}
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <ReactPaginate
        previousLabel={'Previous'}
        nextLabel={'Next'}
        pageCount={pageCount}
        onPageChange={handlePageClick}
        containerClassName={'super-admin-pagination'}
        previousLinkClassName={'super-admin-pagination-link'}
        nextLinkClassName={'super-admin-pagination-link'}
        disabledClassName={'super-admin-pagination-disabled'}
        activeClassName={'super-admin-pagination-active'}
      />
      </div>

      

      {editingUser && (
        <div className="super-admin-permissions-modal">
          <h3><FaEdit className="modal-icon" /> Edit Permissions for {users.find(u => u.id === editingUser)?.name}</h3>
          <div className="super-admin-permissions-grid">
            {AVAILABLE_MODULES.map(module => {
              const user = users.find(u => u.id === editingUser);
              const isChecked = user.permissions.includes(module);
              return (
                <label key={module} className="super-admin-permission-item">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handlePermissionToggle(editingUser, module)}
                  />
                  {module}
                </label>
              );
            })}
          </div>
        </div>
      )}

      <ConfirmationDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
      />
    </div>
  );
};

export default UserManagement;